import type { LatLng } from '@bilmat/shared';

export type SamplingParams = {
  path: LatLng[];
  totalDurationSec: number;
  earliestMin: number;
  latestMin: number;
};

export type SamplingResult = {
  points: LatLng[];
  radiusMeters: number;
};

/**
 * Väljer sökpunkter längs rutten inom det valda tidsfönstret.
 * 1 punkt per ~20 min (min 3, max 20). Radie 5–10 km, skalar med spann.
 * Speglar prototypens findRestaurantsAlongRoute().
 */
export function sampleSearchPoints({
  path,
  totalDurationSec,
  earliestMin,
  latestMin,
}: SamplingParams): SamplingResult {
  const totalMin = totalDurationSec / 60;
  const minPercent = totalMin > 0 ? earliestMin / totalMin : 0;
  const maxPercent = totalMin > 0 ? latestMin / totalMin : 1;
  const spanMinutes = (maxPercent - minPercent) * totalMin;

  const numPoints = Math.min(20, Math.max(3, Math.round(spanMinutes / 20)));
  const radiusMeters = Math.min(10000, Math.max(5000, Math.round(spanMinutes * 40)));

  const points: LatLng[] = [];
  for (let i = 0; i < numPoints; i++) {
    const percent =
      numPoints === 1 ? minPercent : minPercent + (maxPercent - minPercent) * (i / (numPoints - 1));
    const index = Math.floor(path.length * Math.min(percent, 0.99));
    const point = path[Math.max(0, Math.min(index, path.length - 1))];
    if (point) points.push(point);
  }
  return { points, radiusMeters };
}

/** Index för den rutt-punkt som ligger närmast en plats (kvadrerat avstånd). */
export function closestRouteIndex(loc: LatLng, path: LatLng[]): number {
  let bestIndex = 0;
  let bestDist = Infinity;
  for (let i = 0; i < path.length; i++) {
    const p = path[i];
    if (!p) continue;
    const dlat = loc.lat - p.lat;
    const dlng = loc.lng - p.lng;
    const d = dlat * dlat + dlng * dlng;
    if (d < bestDist) {
      bestDist = d;
      bestIndex = i;
    }
  }
  return bestIndex;
}

/** Deduplicerar på placeId och sorterar grovt längs rutten; tar top N kandidater. */
export function dedupeAndOrder<T extends { placeId: string; location: LatLng }>(
  places: T[],
  path: LatLng[],
  limit: number,
): T[] {
  const seen = new Set<string>();
  const unique: T[] = [];
  for (const p of places) {
    if (p.placeId && !seen.has(p.placeId)) {
      seen.add(p.placeId);
      unique.push(p);
    }
  }
  unique.sort((a, b) => closestRouteIndex(a.location, path) - closestRouteIndex(b.location, path));
  return unique.slice(0, limit);
}
